<template>
    <div class="vrm-graph-preview">
        <div v-if="!parametersSelected" class="vrm-graph-preview--no-parameters">
            <span>
                {{
                    $t('custom_widgets.display_graph_after_selecting_parameters', {
                        i18nFallback: 'Graph will be displayed after selecting the parameter(s)',
                    })
                }}
            </span>
        </div>
        <div v-if="parametersSelected && !loadedGraphData" class="vrm-graph-preview--loading">
            <span>{{ $t('core.loading', { i18nFallback: 'Loading...' }) }}</span>
        </div>
        <div
            v-if="parametersSelected && loadedGraphData"
            class="vrm-gridster vrm-graph-preview--graph"
        >
            <div class="vrm-gridster__item">
                <graph-custom-widget
                    :widget-id="0"
                    :installation="installation"
                    :data="graphData"
                    :title="widget.title"
                    :series="series"
                    :range="range"
                    :is-advanced-view="isAdvancedView"
                    :is-ai-assistant="isAiAssistant"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import GraphCustomWidget from '@/vue/components/shared/widget/GraphCustomWidget';
import { ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: {
        GraphCustomWidget,
    },
})
@AngularLocaleBundle('core')
@AngularLocaleBundle('custom_widgets')
export default class GraphPreview extends Vue {
    @Prop()
    widget;
    @Prop()
    installation;
    @Prop()
    startDate;
    @Prop()
    endDate;
    @Prop({ type: Boolean, default: false })
    isAdvancedView;
    @Prop({ type: Boolean, default: false })
    isAiAssistant;

    @ngInject()
    ApiService;

    loadedGraphData = false;
    series = [];
    graphData = {
        data: {},
        meta: {},
        yAxisProperties: {},
    };

    get parametersSelected() {
        return !(
            this.widget.parameters.length === 0 ||
            this.widget.parameters.filter(p => p.dataAttribute).length === 0
        );
    }

    get range() {
        const startDate = this.startDate ? this.startDate : undefined;
        const endDate = this.endDate ? this.endDate : undefined;
        return {
            ...(startDate && { startDate, reloadStartDate: startDate }),
            ...(endDate && { endDate, reloadEndDate: endDate }),
        };
    }

    @Watch('widget.parameters', { deep: true })
    @Watch('widget.yAxisProperties', { deep: true })
    onWidgetChanged() {
        // Debounce, graph size changing in particular can be very annoying. Not to mention performance cost.
        this.debouncedUpdate(this);
    }

    debouncedUpdate = _.debounce(context => {
        if (context.parametersSelected) {
            context.updateGraph();
        }
    }, 500);

    updateGraph() {
        this.loadedGraphData = false;
        this.getGraphData().then(() => {
            this.updateGraphAxis();
            this.series = this.seriesFromParameters(this.widget.parameters);
            this.loadedGraphData = true;
        });
    }

    async getGraphData() {
        const promises = [];
        const groupedParameters = _.groupBy(
            this.widget.parameters.filter(({ dataAttribute }) => {
                return (
                    // replace all attributes, even existing ones to keep timestamps synchronized
                    dataAttribute
                );
            }),
            parameter => parameter.device.instance,
        );

        _.forEach(groupedParameters, (group, instance) => {
            const attributeCodes = group.map(p => p.dataAttribute.code).join('&attributeCodes[]=');
            const url = `&instance=${instance}&attributeCodes[]=${attributeCodes}`;
            promises.push(this.loadGraphData(this.installation.id, url));
        });

        return Promise.all(promises).then(responses => {
            _.forEach(responses, ({ data }) => {
                if (data && data.success) this.addGraphData(data.records);
            });
        });
    }

    async loadGraphData(installationId, url) {
        const startDate = this.startDate ? `&start=${this.startDate}` : '';
        const endDate = this.endDate ? `&end=${this.endDate}` : '';
        return this.ApiService.installations(
            installationId,
            'widgets/Graph?pointsPerPixel=1&useMinMax=' +
                (this.isAiAssistant ? 1 : 0) +
                url +
                startDate +
                endDate,
        );
    }

    addGraphData(records) {
        _.forEach(records.data, (data, attribute) => {
            const key = `${attribute}_${records.instance}`;
            const code = `${records.meta[attribute].code}_${records.instance}`;
            this.graphData.data[key] = data;
            this.graphData.meta[key] = { ...records.meta[attribute], code };
        });
    }

    updateGraphAxis() {
        this.graphData.yAxisProperties = {
            method: this.widget.yAxisProperties.method.value,
            globalMin: this.widget.yAxisProperties.min,
            globalMax: this.widget.yAxisProperties.max,
            globalInterval: this.widget.yAxisProperties.interval,
            attributeProperties: _.reduce(
                this.widget.parameters,
                (array, parameter) => {
                    if (parameter.dataAttribute) {
                        array[`${parameter.dataAttribute.code}_${parameter.device.instance}`] =
                            parameter.yAxisProperties;
                    }
                    return array;
                },
                [],
            ),
        };
    }

    seriesFromParameters(parameters) {
        const series = [];
        for (const parameter in parameters) {
            let currentParameter = this.widget.parameters[parameter];
            if (!currentParameter.dataAttribute) continue;
            series.push({
                serieColor: currentParameter.label.value,
                code: currentParameter.dataAttribute.code,
                idDataAttribute: currentParameter.dataAttribute.idDataAttribute,
                deviceName: currentParameter.device.productName || currentParameter.device.name,
                instance: currentParameter.device.instance,
                class: currentParameter.device.iconClass,
            });
        }
        return series;
    }
}
</script>
